import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useVectorModal } from '../../../hooks/useVectorMemoryModal';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  type ChipProps,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CaretDown,
  Check,
  PencilSimple,
  TrashSimple,
  X,
} from '@phosphor-icons/react';
import JsonView from 'react18-json-view';
import { UploadItemDimensions } from './UploadItemDimensions';
import { UploadItemMetadatas } from './UploadItemMetadatas';
import { VectorMetadataItem } from './VectorMetadataItem';

import { selectChunkingOptions } from '.';
import { getChunkingByHeadingPreview } from '../../../api/getChunkingByHeadingPreview';

import type { NamespaceFileStatus } from '../../../types/namespace';
import type { VectorChunkingMethod } from '../../../types/vector';
import type { VectorFileData } from './vectorModal';

interface UploadItemProps extends Omit<VectorFileData, 'fileUrl'> {
  fileUrl?: string;
  checked: boolean;
  status?: NamespaceFileStatus;
  onSelectedFilesChange: (fileId: string) => void;
}

const itemStatusMap: Record<
  Exclude<NamespaceFileStatus, ''>,
  ChipProps['color']
> = {
  Enqueued: 'warning',
  Error: 'error',
  Processing: 'primary',
};

function UploadItem({
  checked,
  chunking,
  customFileName,
  fileName,
  fileUrl,
  id,
  metadatas,
  dimensions,
  headingLevel: headingLevelProps = 1,
  status,
  onSelectedFilesChange,
}: UploadItemProps) {
  const {
    tab,

    stagingFiles,
    onStagingFilesChange,

    storedFiles,

    onVectorFileDelete,
  } = useVectorModal();

  const isStoredFile = storedFiles.find(storedFile => storedFile.id === id);
  const selectedChunking = selectChunkingOptions.find(
    ({ value }) => value === chunking,
  );

  const [expanded, setExpanded] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState(customFileName ? customFileName : fileName);
  const [headingLevel, setHeadingLevel] = useState(`${headingLevelProps}`);
  const [showHeadingChunkingPreview, setShowHeadingChunkingPreview] =
    useState(false);

  const handleExpandedToggle = () => {
    setExpanded(prev => !prev);
  };

  const handleNameChange = (newName: string) => {
    setName(newName);
  };

  const handleCustomNameApply = (customName: string) => {
    const newFileList = stagingFiles.map(file => {
      if (file.id === id) {
        return {
          ...file,
          customFileName: customName,
        };
      }

      return file;
    });

    onStagingFilesChange(newFileList);
    setIsEditingName(false);
  };

  const handleChunkingMethodChange = (
    fileId: string,
    newChunkingMethod: VectorChunkingMethod,
  ) => {
    const newFileList = stagingFiles.map(file => {
      if (file.id === fileId) {
        const { headingLevel: fileHeadingLevel, ...rest } = file;

        if (newChunkingMethod === 'byHeading') {
          return {
            ...rest,
            headingLevel: Number(headingLevel),
            chunking: newChunkingMethod,
          };
        }

        return {
          ...rest,
          chunking: newChunkingMethod,
        };
      }

      return file;
    });

    onStagingFilesChange(newFileList);
  };

  const handleHeadingLevelChange = (newHeadingLevel: string) => {
    const newFileList = stagingFiles.map(file => {
      if (file.id === id) {
        return {
          ...file,
          headingLevel: Number(newHeadingLevel),
        };
      }

      return file;
    });

    onStagingFilesChange(newFileList);
    setHeadingLevel(newHeadingLevel);
  };

  const handleShowHeadingChunkingPreviewToggle = () => {
    setShowHeadingChunkingPreview(prev => !prev);
  };

  const { data: chunkingByHeadingPreview, isLoading } = useQuery({
    queryKey: ['vector', 'chunkingByHeading', fileUrl, headingLevel],
    queryFn: () => {
      if (!fileUrl) {
        return;
      }

      return getChunkingByHeadingPreview({
        fileUrl,
        headingLevel: Number(headingLevel),
      });
    },
    enabled: !!fileUrl && selectedChunking?.value === 'byHeading',
  });

  const MAX_METADATAS = 2;

  const hasMetadataOverflow = metadatas?.length > MAX_METADATAS;
  const metadataOverflowAmount = metadatas?.length - MAX_METADATAS;
  const visibleMetadatas = metadatas?.slice(0, MAX_METADATAS);
  const hiddenMetadatas = metadatas?.slice(MAX_METADATAS);

  return (
    <ListItem
      sx={{
        backgroundColor: expanded ? '#fafafa' : '#fff',
        borderRadius: '2rem',
      }}
    >
      <Accordion
        expanded={expanded}
        sx={{
          backgroundColor: 'transparent',

          width: '100%',
          height: 'auto !important',
          minWidth: 'auto !important',
          minHeight: 'auto !important',

          boxShadow: 'none',

          borderRadius: '0 !important',
          borderBlockEndColor: expanded ? 'transparent' : '#e4e4e7',
          borderBlockEndStyle: 'solid',
          borderBlockEndWidth: '1px',

          '&:hover [data-actions]': {
            display: 'block',
          },
        }}
        onChange={handleExpandedToggle}
      >
        <AccordionSummary
          expandIcon={<CaretDown size={20} />}
          sx={{
            minHeight: 'auto',

            '& > .MuiAccordionSummary-content': {
              position: 'relative',
              isolation: 'isolate',

              width: '100%',
              marginBlock: '0.75rem',
              marginInlineEnd: isEditingName ? '1.25rem' : '0',

              display: 'grid',
              gridTemplateColumns: isEditingName
                ? '1fr'
                : expanded
                  ? 'min-content 1fr'
                  : 'min-content 25ch max-content 1fr',
              alignItems: 'center',
              gap: '0.5rem',
            },

            '& > .MuiAccordionSummary-content.Mui-expanded': {
              marginBlock: '0.75rem',
            },

            '&:focus-within': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {isEditingName ? (
            <Box
              sx={{
                width: '100%',
                position: 'relative',

                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <TextField
                fullWidth
                value={name}
                onClick={evt => evt.stopPropagation()}
                onChange={evt => handleNameChange(evt.target.value)}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <IconButton
                  size='small'
                  color='success'
                  aria-label='Apply custom name'
                  onClick={evt => {
                    evt.stopPropagation();
                    handleCustomNameApply(name);
                  }}
                >
                  <Check />
                </IconButton>

                <IconButton
                  size='small'
                  color='error'
                  aria-label='Cancel edit custom name'
                  onClick={evt => {
                    evt.stopPropagation();
                    setIsEditingName(false);
                  }}
                >
                  <X />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <>
              <Checkbox
                sx={{ padding: 0 }}
                checked={checked}
                onClick={evt => evt.stopPropagation()}
                onChange={() => onSelectedFilesChange(id)}
              />

              <Tooltip title={name}>
                <Typography
                  sx={{
                    maxWidth: expanded ? 'auto' : '25ch',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '1rem',
                  }}
                  variant='h3'
                  color='#000'
                  fontWeight={600}
                >
                  {name}
                </Typography>
              </Tooltip>

              {!expanded ? (
                <Typography
                  sx={{
                    backgroundColor: '#f4f4f5',
                    paddingBlock: '0.25rem',
                    paddingInline: '0.5rem',
                    borderRadius: '9999vmax',

                    fontSize: '0.825rem',
                  }}
                >
                  Chunked by {selectedChunking ? selectedChunking.label : null}
                </Typography>
              ) : null}

              {!expanded ? (
                <List
                  sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  {visibleMetadatas.map(({ name, value }) => (
                    <VectorMetadataItem key={name} name={name} value={value} />
                  ))}

                  {hasMetadataOverflow ? (
                    <Tooltip
                      title={
                        <>
                          {hiddenMetadatas.map(({ name, value }) => (
                            <Typography key={name}>
                              {name}:{value}
                            </Typography>
                          ))}
                        </>
                      }
                    >
                      <Typography
                        component='span'
                        sx={{ display: 'inline-block', fontSize: '0.825rem' }}
                      >
                        + {metadataOverflowAmount} more
                      </Typography>
                    </Tooltip>
                  ) : null}
                </List>
              ) : null}

              <Box
                data-actions
                sx={{
                  display: 'none',

                  position: 'absolute',
                  right: '0',

                  paddingInline: '0.5rem',
                }}
              >
                <Button
                  disableRipple
                  disabled={!!isStoredFile}
                  sx={{
                    minWidth: 'auto',
                    paddingBlock: '0.25rem',
                    paddingInline: '0.5rem',

                    borderRadius: '0.5rem',
                  }}
                  onClick={evt => {
                    evt.stopPropagation();
                    setIsEditingName(true);
                  }}
                >
                  <PencilSimple />
                </Button>

                <Button
                  disableRipple
                  sx={{
                    minWidth: 'auto',
                    paddingBlock: '0.25rem',
                    paddingInline: '0.5rem',

                    borderRadius: '0.5rem',

                    '&:hover': {
                      backgroundColor: '#fef2f2',
                    },
                  }}
                  onClick={evt => {
                    evt.stopPropagation();
                    onVectorFileDelete(id);
                  }}
                >
                  <TrashSimple color='#dc2626' />
                </Button>
              </Box>

              {status && status !== 'Finished' && status !== 'Migrated' ? (
                <Chip
                  sx={{
                    position: 'absolute',
                    right: '0',
                    zIndex: 10,
                  }}
                  label={status}
                  size='small'
                  color={itemStatusMap[status]}
                />
              ) : null}
            </>
          )}
        </AccordionSummary>

        <AccordionDetails
          sx={{ '&>*+*': { marginBlockStart: '1.25rem !important' } }}
        >
          <FormControl fullWidth variant='outlined' size='medium'>
            <InputLabel id={`chunking-${id}`} sx={{ color: '#9e9e9e' }}>
              Chunking
            </InputLabel>

            <Select
              id={`chunking-select-${id}`}
              fullWidth
              sx={{
                marginBottom: 0,
              }}
              label='Chunking'
              labelId={`chunking-${id}`}
              value={chunking}
              onChange={evt =>
                handleChunkingMethodChange(
                  id,
                  evt.target.value as VectorChunkingMethod,
                )
              }
              disabled={!!isStoredFile}
            >
              {selectChunkingOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  Chunk by {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {tab !== 'edit-stored-files' && chunking === 'byHeading' ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '0.125rem',
                }}
              >
                <FormControl fullWidth>
                  <InputLabel id='heading-level'>Heading Level</InputLabel>

                  <Select
                    id='heading-level-select'
                    fullWidth
                    sx={{
                      marginBottom: 0,
                    }}
                    label='Heading Level'
                    labelId='heading-level'
                    value={headingLevel}
                    onChange={evt => handleHeadingLevelChange(evt.target.value)}
                  >
                    {[1, 2, 3, 4, 5, 6].map(level => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant='text'
                  sx={{ fontSize: '0.75rem' }}
                  onClick={handleShowHeadingChunkingPreviewToggle}
                >
                  {showHeadingChunkingPreview
                    ? 'Hide chunking preview'
                    : 'Show chunking preview'}
                </Button>
              </Box>

              {!showHeadingChunkingPreview ? null : isLoading ? (
                <Box>
                  <CircularProgress size={16} />
                </Box>
              ) : (
                <Box>
                  <JsonView src={chunkingByHeadingPreview} collapsed={false} />
                </Box>
              )}
            </>
          ) : null}

          <UploadItemMetadatas id={id} fileMetadatas={metadatas} />
          <UploadItemDimensions id={id} fileDimensions={dimensions} />
        </AccordionDetails>
      </Accordion>
    </ListItem>
  );
}

export { UploadItem };
